import React, { ReactElement, useCallback, useEffect, useMemo } from "react";

import { Button, Modal, Segment, SemanticCOLORS } from "semantic-ui-react";

import { useIntl } from "react-intl";

import "react-lib/css/ModInfo.css";

type ModMedReconcileAlertProps = {
  onEvent?: (e: any) => void;
  setProp?: (key: any, value: any, callback?: () => void) => void;

  btnLoading?: boolean;
  buttonColor?: SemanticCOLORS;
  buttonText?: string;
  card?: string;
  children?: ReactElement;
  django?: any;
  medReconcileCheck?: {
    med_reconciliation?: string | null;
    med_reconciliation_label?: string;
    open_card_med_reconciliation?: boolean;
    plan_transfer_id?: string;
    roleText?: string;
  } | null;
  medReconcileIndex?: number;
  open: boolean;
  skipDivisionCheck?: boolean;
  titleColor?: SemanticCOLORS;
  titleName?: string;
  errorMessage?: Record<string, any>;
  // callback
  onApprove?: (data: Record<string, any>) => void;
  onClose?: () => void;
};

const styles = {
  button: { marginTop: "10px" },
  header: { lineHeight: "1.3em" },
  content: {
    color: "red",
    fontSize: "larger",
    fontWeight: "bold",
    lineHeight: 1.5,
  },
};

const ModMedReconcileAlert = (props: ModMedReconcileAlertProps) => {
  console.log("CardDischarge ModMedReconcileAlert props:", props);
  const intl = useIntl();

  // Effect
  useEffect(() => {
    if (props.errorMessage) {
      return;
    }

    props.onEvent?.({
      message: "HandleCheckAndGetMedReconcile",
      params: { skipDivisionCheck: props.skipDivisionCheck },
    });
  }, [props.skipDivisionCheck]);

  // Callback
  const handleApprove = useCallback(() => {
    props.setProp?.("medReconcileCheck.open_card_med_reconciliation", false);

    const params: any = {
      medReconcileCheck: props.medReconcileCheck,
      selectedRecordViewIndex: props.medReconcileIndex,
    };

    if (props.errorMessage) {
      const cardError = props.errorMessage[props.card || ""];
      const error: string = cardError?.error?.[0] || cardError?.[0] || "";

      // *  error string "[123] some text [456]" จะได้ medId = 123 และ enId = 456
      const [medId, enId] = [...error.matchAll(/\[(\d+)/g)].map((match) => Number(match[1]));

      params.medReconcileCheck = { encounter_id: enId, med_reconciliation: medId };

      // Clear error message after handling
      props.setProp?.(`errorMessage.${props.card || ""}`, null);
    }

    if (props.onApprove) {
      props.onApprove(params);
    } else {
      props.onEvent?.({
        message: "OpenMedReconcileFromWarning",
        params,
      });
    }
  }, [
    props.card,
    props.errorMessage,
    props.medReconcileCheck,
    props.medReconcileIndex,
    props.onApprove,
  ]);

  // Memo
  const alertContent = useMemo(() => {
    if (props.errorMessage) {
      const cardError = props.errorMessage[props.card || ""];

      return (cardError?.error?.[0] || cardError?.[0] || "") as string;
    }

    const medReconciliation = props.medReconcileCheck?.med_reconciliation;

    if (medReconciliation !== null && medReconciliation !== undefined) {
      const roleTypes: string[] = props.django?.user?.role_types || [];

      let role = "";

      if (roleTypes.includes("DOCTOR")) {
        role = intl.formatMessage({ id: "แพทย์" });
      } else if (roleTypes.includes("REGISTERED_NURSE")) {
        role = intl.formatMessage({ id: "พยาบาล" });
      } else if (roleTypes.includes("PHARMACIST")) {
        role = intl.formatMessage({ id: "เภสัชกร" });
      }

      if (props.medReconcileCheck?.roleText) { 
        switch(props.medReconcileCheck?.roleText) {
        case "doctor":
          role = "แพทย์"
          break; 
        case "nurse":
          role = "พยาบาล"
          break;
        case "ward":
          role = "พยาบาลผู้ป่วยใน"
          break;
        case "pharma":
          role = "เภสัชกร"
          break;
        default:
          break;
        }
      }

      return `Med Reconcile ${
        props.medReconcileCheck?.med_reconciliation_label || ""
      } ยังไม่ได้ถูกรับทราบโดย${role}`;
    }

    return `ยังไม่ได้สร้าง Medication Reconciliation สำหรับ PlanTransfer ${props.medReconcileCheck?.plan_transfer_id}`;
  }, [props.card, props.django?.user?.role_types, props.errorMessage, props.medReconcileCheck]);

  return (
    <Modal open={props.open} size="mini" onClose={props.onClose}>
      <Segment
        className={`modHeader ${props.titleColor || "yellow"}`}
        style={styles.header}
        inverted
      >
        {props.titleName || "แจ้งเตือน"}
      </Segment>
      <Segment align="center" className="modContent" padded>
        <div style={styles.content}>{props.children ?? alertContent}</div>
        <Button
          className="approveButton"
          color={props.buttonColor || "green"}
          loading={props.btnLoading}
          style={styles.button}
          basic
          onClick={handleApprove}
        >
          {props.buttonText || "ตกลง"}
        </Button>
      </Segment>
    </Modal>
  );
};

ModMedReconcileAlert.displayName = "ModMedReconcileAlert";

export default React.memo(ModMedReconcileAlert);
